/* tt-cart-peek.js
   The cart as a popover, opened from the sticky bar's button and from the header's bag icon.
   Owned by scripts/retail_b2b_features.py.

   ✅ **EDITABLE SINCE 2026-09-30.** Hajo: *"the cart is not updateable in the cart itself,
   which i need it to be."* Measured that evening: `/cart` was already fully editable, steppers,
   bin icon and all, and the cart he actually meets is THIS panel, which printed `4 x €19.67`
   as text. So the ask was not about the cart page, it was about the cart he can reach from
   anywhere. Every line now carries a stepper and a Remove.

   🛑 **IT CHANGES LINES BY `key`, NOT BY LINE NUMBER AND NOT BY VARIANT ID.**
   - A line NUMBER shifts the moment another line is removed, so a second click lands on a
     different product than the one under the cursor. Nothing errors and the wrong thing changes.
   - A VARIANT ID is not unique in this cart: the starterpacks post the same pack variant with
     different colours in line item properties, so two pack lines share one variant and
     `/cart/update.js` would collapse them.
   `key` is stable per line, properties included, and `/cart/change.js` takes it as `id`.

   ⚠️ **Requests are serialised.** `/cart/change.js` returns the whole cart, so the panel
   re-renders from the response rather than from what it believed a moment ago; two fast clicks
   queue instead of racing. */
(function () {
  if (customElements.get('tt-cart-peek')) return;

  customElements.define('tt-cart-peek', class extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-ttcp-body]');
      this.foot = this.querySelector('[data-ttcp-foot]');
      this.subtotalEl = this.querySelector('[data-ttcp-subtotal]');
      this.countEl = this.querySelector('[data-ttcp-count]');
      this.busy = false;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-ttcp-close]')) return this.close();
        const rm = e.target.closest('[data-ttcp-remove]');
        if (rm) return this.change(rm.dataset.key, 0);
        const step = e.target.closest('[data-ttcp-step]');
        if (step) {
          const line = step.closest('.ttcp__line');
          const input = line.querySelector('[data-ttcp-input]');
          const next = (parseInt(input.value, 10) || 0) + parseInt(step.dataset.ttcpStep, 10);
          return this.change(line.dataset.key, next < 0 ? 0 : next);
        }
      });

      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-ttcp-input]');
        if (!input) return;
        const line = input.closest('.ttcp__line');
        this.change(line.dataset.key, Math.max(0, parseInt(input.value, 10) || 0));
      });

      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.hidden) this.close(); });

      // Anything, anywhere, with data-tt-cart-peek. Delegated so a bar rendered later still works.
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-tt-cart-peek]');
        if (t) { e.preventDefault(); this.open(); return; }
        // The header's bag icon opens the same panel rather than navigating.
        const bag = e.target.closest('#cart-icon-bubble');
        if (bag && !e.metaKey && !e.ctrlKey) { e.preventDefault(); this.open(); }
      });

      // Another surface changed the cart (the bulk grid's Update button): repaint if we are open.
      document.addEventListener('cart:refresh', () => { if (!this.hidden) this.reload(); });
    }

    root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }

    money(cents) {
      const cur = this.dataset.currency || 'EUR';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en',
          { style: 'currency', currency: cur }).format(cents / 100);
      } catch (e) { return (cents / 100).toFixed(2); }
    }

    async open() {
      this.hidden = false;
      document.documentElement.classList.add('tt-peek-open');
      this.body.innerHTML = '<p class="ttcp__empty">' + (this.dataset.loading || 'Loading') + '</p>';
      this.reload();
    }

    async reload() {
      try {
        this.render(await (await fetch(this.root() + 'cart.js')).json());
      } catch (e) {
        this.body.innerHTML = '<p class="ttcp__empty">The cart could not be read.</p>';
      }
    }

    close() {
      this.hidden = true;
      document.documentElement.classList.remove('tt-peek-open');
    }

    /** One line to an absolute quantity. 0 removes it. */
    async change(key, quantity) {
      if (this.busy || !key) return;
      this.busy = true;
      this.classList.add('is-busy');
      try {
        const res = await fetch(this.root() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        const cart = await res.json();
        if (!res.ok) {
          this.warn(cart.description || cart.message || 'That quantity is not available.');
          await this.reload();
        } else {
          this.render(cart);
        }
        // 🔑 Tell the rest of the store. The bulk grid fills its inputs and badges from the
        // cart, so without this a change here leaves the grid behind it showing the old numbers.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        this.syncBubble(cart.item_count);
      } catch (e) {
        this.warn('The cart could not be updated.');
      } finally {
        this.busy = false;
        this.classList.remove('is-busy');
      }
    }

    warn(text) {
      let el = this.querySelector('[data-ttcp-warn]');
      if (!el) {
        el = document.createElement('p');
        el.className = 'ttcp__warn';
        el.setAttribute('data-ttcp-warn', '');
        el.setAttribute('role', 'status');
        this.body.parentNode.insertBefore(el, this.body);
      }
      el.textContent = text;
      el.hidden = false;
      clearTimeout(this._warnT);
      this._warnT = setTimeout(() => { el.hidden = true; }, 5000);
    }

    /** The header's own count, so the panel and the bag icon never disagree. */
    syncBubble(count) {
      const bubble = document.querySelector('#cart-icon-bubble');
      if (!bubble) return;
      const n = bubble.querySelector('.cart-count-bubble span:first-child, [data-cart-count]');
      if (n) n.textContent = count;
      const wrap = bubble.querySelector('.cart-count-bubble');
      if (wrap) wrap.hidden = count === 0;
    }

    render(cart) {
      if (this.countEl) {
        this.countEl.textContent = cart.item_count
          ? cart.item_count + (cart.item_count === 1 ? ' item' : ' items') : '';
      }
      if (!cart.item_count) {
        this.body.innerHTML = '<p class="ttcp__empty">Your cart is empty.</p>';
        this.foot.hidden = true;
        return;
      }
      this.foot.hidden = false;
      this.subtotalEl.textContent = this.money(cart.total_price);
      const frag = document.createDocumentFragment();
      cart.items.forEach((li) => {
        const row = document.createElement('div');
        row.className = 'ttcp__line';
        row.dataset.key = li.key;

        const img = document.createElement('span');
        img.className = 'ttcp__img';
        if (li.image) {
          const i = document.createElement('img');
          i.src = li.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2');
          i.alt = '';
          i.loading = 'lazy';
          img.appendChild(i);
        }

        const text = document.createElement('span');
        text.className = 'ttcp__text';
        const t = document.createElement('span');
        t.className = 'ttcp__name';
        t.textContent = li.product_title;
        const v = document.createElement('span');
        v.className = 'ttcp__meta';
        // ⚠️ The quantity is NOT repeated here any more. It is in the stepper below, and
        // printing "4 x" beside a field that says 4 is two numbers that can disagree mid-request.
        v.textContent = (li.variant_title && li.variant_title !== 'Default Title'
          ? li.variant_title + ' / ' : '') + this.money(li.price) + ' each';
        text.appendChild(t); text.appendChild(v);

        // The starterpack carries its chosen colours as line item properties, so they show here.
        if (li.properties && Object.keys(li.properties).length) {
          Object.keys(li.properties).forEach((k) => {
            if (!li.properties[k] || k.charAt(0) === '_') return;
            const pr = document.createElement('span');
            pr.className = 'ttcp__prop';
            pr.textContent = k + ': ' + li.properties[k];
            text.appendChild(pr);
          });
        }

        const qty = document.createElement('span');
        qty.className = 'ttcp__qty';
        const minus = document.createElement('button');
        minus.type = 'button';
        minus.className = 'ttcp__step';
        minus.dataset.ttcpStep = '-1';
        minus.setAttribute('aria-label', 'Decrease quantity for ' + li.product_title);
        minus.textContent = '\u2212';
        const input = document.createElement('input');
        input.type = 'number';
        input.className = 'ttcp__input';
        input.inputMode = 'numeric';
        input.min = '0';
        input.value = li.quantity;
        input.setAttribute('data-ttcp-input', '');
        input.setAttribute('aria-label', 'Quantity for ' + li.product_title);
        const plus = document.createElement('button');
        plus.type = 'button';
        plus.className = 'ttcp__step';
        plus.dataset.ttcpStep = '1';
        plus.setAttribute('aria-label', 'Increase quantity for ' + li.product_title);
        plus.textContent = '+';
        qty.appendChild(minus); qty.appendChild(input); qty.appendChild(plus);
        text.appendChild(qty);

        const right = document.createElement('span');
        right.className = 'ttcp__right';
        const price = document.createElement('span');
        price.className = 'ttcp__price';
        price.textContent = this.money(li.line_price);
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'ttcp__remove';
        rm.dataset.key = li.key;
        rm.setAttribute('data-ttcp-remove', '');
        rm.textContent = 'Remove';
        right.appendChild(price); right.appendChild(rm);

        row.appendChild(img); row.appendChild(text); row.appendChild(right);
        frag.appendChild(row);
      });
      this.body.innerHTML = '';
      this.body.appendChild(frag);
    }
  });
})();
